import { DateTime } from "luxon";
import { Notification } from "ant-design-vue";
import { countryList } from "./countryList";
import { highRiskCountryList } from "./highRiskCountryList";

const logSuccess = res => {
  /* eslint-disable-next-line */
  console.log(
    "%c Request Success: %s",
    "color: #4CAF50; font-weight: bold",
    res.config.url,
    res
  );
  return res;
};

const logError = err => {
  /* eslint-disable-next-line */
  console.log("%c Request Error:", "color: #EC6060; font-weight: bold", err);
  return Promise.reject(err);
};

const displayErrors = err => {
  if (err.response) {
    const { message, errors } = err.response.data;
    if (typeof errors === "object") {
      Object.entries(errors).forEach(([key, msgs]) => {
        let description = "";
        if (Array.isArray(msgs)) {
          description = h => msgs.map(n => [n, h("br")]);
        } else {
          description = msgs;
        }
        Notification.error({
          // capitalize key
          message: key.charAt(0).toLocaleUpperCase() + key.slice(1),
          description,
          placement: "bottomRight"
        });
      });
    } else {
      Notification.error({
        message: "Error",
        description: message,
        placement: "bottomRight"
      });
    }
  } else {
    Notification.error({
      message: "Error",
      description: err.message,
      placement: "bottomRight"
    });
  }
};

const dtNow = DateTime.local();
const dtLastMonth = dtNow.minus({ months: 1 });

const dateConfig = {
  moFormat: "YYYY-MM-DDTHH:mm:ss.SSSSSS[Z]",
  dtFormat: "yyyy-MM-dd'T'HH:mm:ss.u'000Z'",
  dtNow,
  dtLastMonth,
  dtDateOptions: [
    [dtNow.startOf("day"), ""], // today
    [dtNow.minus({ days: 1 }).startOf("day"), ""], // yesterday
    [dtNow.minus({ days: 7 }).startOf("day"), ""], // last 7 days
    [dtNow.minus({ days: 30 }).startOf("day"), ""], // last 30 days
    [dtNow.startOf("month"), ""], // this month
    [dtLastMonth.startOf("month"), dtLastMonth.endOf("month")], // last month
    [DateTime.fromMillis(0), ""] // all
  ],
  dateOptions: [
    "Today",
    "Yesterday",
    "Last 7 Days",
    "Last 30 Days",
    "This Month",
    "Last Month",
    "All"
  ]
};

const getStartDate = () => {
  const { dtDateOptions, dtFormat } = dateConfig;
  return dtDateOptions[0][0].toUTC().toFormat(dtFormat);
};

const apiConfig = {
  store: {
    token: "",
    envConfig: {
      development: "devapi",
      sandbox: "sandboxapi",
      production: "api"
    },
    domain: "legendtrading.com",
    env: process.env.NODE_ENV
  },
  reHostname: /^localhost|127\.0\.0\.1|::1$/i,
  reDevHostname: /^(dev|fe|sb)\w*\.legendtrading\.com$/i,
  get env() {
    return this.store.env;
  },
  set env(val) {
    this.store.env = val;
  },
  get token() {
    return this.store.token;
  },
  set token(val) {
    this.store.token = val;
  },
  get remote() {
    return this.store.envConfig.development;
  },
  set remote(val) {
    if (val) {
      this.store.envConfig.development = val;
    }
  },
  get baseUrl() {
    const { envConfig, domain } = this.store;
    return `https://${envConfig[this.env]}.${domain}`;
  },
  get baseUrlTrade() {
    return `${this.baseUrl}/ex/`;
  },
  get baseUrlKyc() {
    return `${this.baseUrl}/kyc/`;
  }
};

const getCountryList = () => {
  let countryArr = [];

  countryList?.forEach(item => {
    const lowRiskArr = item?.lists?.filter(
      lowRiskItem =>
        !highRiskCountryList.some(
          highRiskItem => lowRiskItem.ISO2 === highRiskItem.ISO2
        )
    );

    countryArr.push({
      letter: item.letter,
      lists: lowRiskArr
    });
  });

  return countryArr;
};

const highRiskTelCountries = [
  "CU",
  "IQ",
  "LY",
  "KP",
  "SS",
  "YE",
  "IR",
  "LB",
  "PK",
  "SY",
  "RU",
  "SD",
  "UA"
];

const partnerOptionList = [
  {
    key: "kucoin",
    name: "KUCOIN",
    primaryColors: {
      color: "#23af91",
      color01: "rgba(35, 175, 145, 0.1)"
    },
    smallView: require(`./assets/img/cases/kucoin-view-small.png`),
    logo: require(`./assets/logo-partners/Exchanges Wallets/Kucoin.png`),
    desktop: require("./assets/img/cases/kucoin-desktop.png"),
    mobile: require("./assets/img/cases/kucoin-mobile.png"),
    desc:
      "KuCoin's integration with Legend Pay's fiat service enables users to buy and sell cryptocurrency easily using their bank accounts or credit cards. Legend Pay offers institutional-grade trading services, ensuring users have access to the best pricing and deep liquidity. This allows for trades up to $20MM in size to be executed in a single trade while maintaining compliance.",
    title: "Unlock the full potential of growth",
    subTitle:
      "KuCoin has partnered with Legend Trading to enable their users easily and securely buy or sell crypto using their bank account or credit card directly on KuCoin platform.",
    integration: {
      title:
        "KuCoin's integration with Legend Pay's fiat service enables users to buy and sell cryptocurrency easily using their bank accounts or credit cards. Legend Pay offers institutional-grade trading services, ensuring users have access to the best pricing and deep liquidity. This allows for trades up to $20MM in size to be executed in a single trade while maintaining compliance.",
      list: [
        {
          title: "Convenient Payment Options:",
          content:
            "We offer diverse payment options such as bank transfers and credit cards(Wire, Swift, SEPA, Faster Payments, Visa, and MasterCard)."
        },
        {
          title: "Institutional-Grade Trading Services:",
          content:
            "Our services provide customers with the best pricing to buy and sell cryptocurrency. This advantage can help to attract more sophisticated investors who are looking for high-quality trading services."
        },
        {
          title: "Wide Currency Support:",
          content:
            "With support for major fiat currencies, customers have a variety of options to choose from, making it easier to buy crypto using their local currency."
        }
      ]
    },
    introduce: {
      title: "About KuCoin",
      subTitle:
        "KuCoin is a cryptocurrency exchange founded in 2017, headquartered in Seychelles, and operating globally. The platform offers a wide variety of cryptocurrencies for trading, including popular coins like Bitcoin, Ethereum, and Litecoin, as well as many lesser-known altcoins. KuCoin also offers features such as margin trading and staking, allowing users to earn rewards on their holdings. The exchange has gained a reputation for being user-friendly, with a simple and intuitive interface, as well as for having strong security measures in place."
    },
    href: "https://www.kucoin.com/"
  },
  {
    key: "gateio",
    name: "GATE.IO",
    primaryColors: {
      color: "#2354e6",
      color01: "rgba(35, 84, 230, 0.1)"
    },
    smallView: require(`./assets/img/cases/gateio-view-small.png`),
    logo: require(`./assets/logo-partners/Exchanges Wallets/Gate.png`),
    desktop: require("./assets/img/cases/gateio-desktop.png"),
    mobile: require("./assets/img/cases/gateio-mobile.png"),
    desc:
      "Gate.io users now have the convenient option to buy and sell cryptocurrency using their bank accounts or credit cards through the integration of Legend Pay's fiat service. This service offers various payment methods, including bank transfers and credit cards (Wire, Swift, SEPA, Faster Payments, Visa, and MasterCard), in addition to institutional-grade trading with optimal pricing and deep liquidity.",
    title: "Revolutionized Crypto Transaction",
    subTitle:
      "Gate.io has teamed up with Legend Trading to facilitate the purchasing and selling of cryptocurrency via bank accounts or credit cards in a safe and simple manner directly on the Gate.io platform for their respective customers.",
    integration: {
      title:
        "Gate.io users now have the convenient option to buy and sell cryptocurrency using their bank accounts or credit cards through the integration of Legend Pay's fiat service. This service offers various payment methods, including bank transfers and credit cards (Wire, Swift, SEPA, Faster Payments, Visa, and MasterCard), in addition to institutional-grade trading with optimal pricing and deep liquidity.",
      list: [
        {
          title: "The most competitive pricing:",
          content:
            "Our platform offers your users compliant access to the most competitive pricing for fiat-to-crypto trades, with deep liquidity available for trades up to $20MM in size for a single transaction."
        },
        {
          title: "Convenient Payment Options:",
          content:
            "We offer diverse payment options such as bank transfers and credit cards(Wire, Swift, SEPA, Faster Payments, Visa, and MasterCard)."
        },
        {
          title: "Global coverage and local payment methods:",
          content:
            "We offer global coverage and accept all payment methods, including major local options. With support for multiple fiat currencies, including USD, EUR, GBP, AUD, CHF, CAD, JPY, and more, we operate in over 150 jurisdictions."
        }
      ]
    },
    introduce: {
      title: "About Gate.io",
      subTitle:
        "Gate.io is a global cryptocurrency exchange that was founded in 2017 and is based in the Cayman Islands. The platform offers a wide range of cryptocurrencies for trading, including popular coins like Bitcoin, Ethereum, and Litecoin, as well as many lesser-known altcoins. Gate.io is known for its user-friendly interface and strong security features, including two-factor authentication and cold storage for user funds. The exchange also offers margin trading and staking services, allowing users to earn rewards on their holdings. "
    },
    href: "https://www.gate.io/en/"
  },
  {
    key: "phemex",
    name: "Phemex",
    primaryColors: {
      color: "#095be0",
      color01: "rgba(9, 91, 224, 0.1)"
    },
    smallView: require(`./assets/img/cases/phemex-view-small.png`),
    logo: require(`./assets/logo-partners/Exchanges Wallets/Phemex.png`),
    desktop: require("./assets/img/cases/phemex-desktop.png"),
    mobile: require("./assets/img/cases/phemex-mobile.png"),
    desc:
      "The seamless integration between Phemex and Legend Trading now allows Phemex users to trade cryptocurrencies with lightning-fast fiat settlements. Leveraging Legend's proprietary trading technology, users can benefit from unparalleled price discovery and make trades that maximize their potential profits.",
    title: "Buy and Sell Crypto with Ease",
    subTitle:
      "Get ready to trade crypto like a pro! Phemex and Legend Trading have joined forces to bring you an all-in-one platform for buying and selling cryptocurrencies. The services support bank transfers and credit cards, and multiple fiat currencies, including USD, EUR, GBP, AUD, CHF, CAD, JPY, and more. ",
    integration: {
      title:
        "The seamless integration between Phemex and Legend Trading now allows Phemex users to trade cryptocurrencies with lightning-fast fiat settlements. Leveraging Legend's proprietary trading technology, users can benefit from unparalleled price discovery and make trades that maximize their potential profits.",
      list: [
        {
          title: "KYC Connect:",
          content:
            "Start onboarding users to buy and sell crypto with ease using KYC Connect. With just one click, users can begin onramp and avoid the hassle of duplicate onboarding procedures. KYC Connect allows you to onboard up to 20x more users, enabling better growth for your platform."
        },
        {
          title: "Global Payment Options:",
          content:
            "Offer various payment methods like bank transfers and credit cards (Wire, Swift, SEPA, Faster Payments, Visa, and MasterCard)"
        },
        {
          title: "The most competitive pricing:",
          content:
            "Leveraging Legend's proprietary trading technology, we provide compliant access to the most competitive pricing for fiat-to-crypto trades.          "
        }
      ]
    },
    introduce: {
      title: "About Phemex",
      subTitle:
        "Phemex is a cryptocurrency derivatives trading platform founded in 2019. It is headquartered in Singapore and is led by a team of former executives from Morgan Stanley and other financial institutions. Phemex allows users to trade cryptocurrency derivatives, such as futures contracts and options, with up to 100x leverage. The platform supports several cryptocurrencies, including Bitcoin, Ethereum, XRP, and Litecoin, among others. Phemex also offers a range of trading tools and features, such as advanced order types and a trading API, to help users execute their trading strategies more effectively. Additionally, Phemex prioritizes security and employs a range of measures to protect users' assets and personal information."
    },
    href: "https://phemex.com/"
  },
  // {
  //   key: "bybit",
  //   name: "BYBIT",
  //   primaryColors: {
  //     color: "#f7a600",
  //     color01: "rgba(247, 166, 0, 0.1)"
  //   },
  //   smallView: require(`./assets/img/cases/bybit-view-small.png`),
  //   logo: require(`./assets/logo-partners/Exchanges Wallets/ByBit.png`),
  //   desktop: require("./assets/img/cases/bybit-desktop.png"),
  //   mobile: require("./assets/img/cases/bybit-mobile.png"),
  //   desc:
  //     "Bybit and Legend Trading have teamed up to provide a unified platform for buying and selling cryptocurrencies, while allowing users to link their bank accounts to crypto. This partnership also offers competitive pricing for buying crypto. Additionally, Legend Pay's fiat service provides multiple payment methods, including bank transfers and credit cards such as Visa and MasterCard.",
  //   title: "Boost Crypto, Quick Fiat Transfers",
  //   subTitle:
  //     "Prepare for professional crypto trading! Bybit and Legend Trading have collaborated to enable linking of your bank account to crypto. This offers you a unified platform for purchasing and selling cryptocurrencies, with the added benefit of favorable pricing for buying crypto",
  //   integration: {
  //     title:
  //       "Bybit and Legend Trading have teamed up to provide a unified platform for buying and selling cryptocurrencies, while allowing users to link their bank accounts to crypto. This partnership also offers competitive pricing for buying crypto. Additionally, Legend Pay's fiat service provides multiple payment methods, including bank transfers and credit cards such as Visa and MasterCard.",
  //     list: [
  //       {
  //         title: "",
  //         content:
  //           "We offers a range of payment options, including bank transfers and credit cards such as Wire, Swift, SEPA, Faster Payments, Visa, and MasterCard for customer convenience."
  //       },
  //       {
  //         title: "",
  //         content:
  //           "The services provide institutional-grade trading services that offer competitive pricing, which can attract more sophisticated investors seeking high-quality trading services."
  //       },
  //       {
  //         title: "",
  //         content:
  //           "Our platform supports major fiat currencies, giving customers a range of options to purchase crypto with their local currency. This makes it easier for customers to buy cryptocurrency."
  //       }
  //     ]
  //   },
  //   introduce: {
  //     title: "About Bybit",
  //     subTitle:
  //       "Bybit is a cryptocurrency derivatives exchange that allows traders to buy and sell various cryptocurrency products with leverage. It was founded in March 2018. Bybit's main products include perpetual contracts, futures contracts, and options contracts, which can be traded with up to 100x leverage. Bybit's trading platform is user-friendly and features advanced trading tools such as order types, charting tools, and risk management features. Additionally, Bybit provides its users with 24/7 customer support and offers multiple security measures, including multi-signature cold wallets, DDoS protection, and two-factor authentication.Bybit supports multiple cryptocurrencies such as Bitcoin (BTC), Ethereum (ETH), Ripple (XRP), EOS, and Bitcoin Cash (BCH), among others. The exchange is popular among professional traders due to its high liquidity, competitive trading fees, and advanced trading tools."
  //   },
  //   href: "https://www.bybit.com/"
  // },
  {
    key: "cobo",
    name: "COBO",
    primaryColors: {
      color: "#4d84ff",
      color01: "rgba(77, 132, 255, 0.1)"
    },
    smallView: require(`./assets/img/cases/cobo-view-small.png`),
    logo: require(`./assets/logo-partners/Exchanges Wallets/Cobo Wallet.png`),
    desktop: require("./assets/img/cases/cobo-desktop.png"),
    mobile: require("./assets/img/cases/cobo-mobile.png"),
    desc:
      "Crypto custody solution Cobo has integrated with Legend Pay to provide institutional clients with a seamless and secure way to connect their bank accounts and buy/sell digital assets. With various payment options available, including Swift, Wire, SEPA, and Faster Payments, the collaboration streamlines the crypto trading process, offering competitive pricing and deep liquidity for institutional investors, and improving their flexibility and security in managing digital assets.",
    title: "Transform Your Vault with Legend Pay",
    subTitle:
      "Crypto vault Cobo partners with Legend Trading to offer institutional clients seamless access to digital assets, competitive pricing, and deep liquidity. With trades up to $20MM in a single transaction and regulatory compliance.",
    integration: {
      title:
        "Crypto custody solution Cobo has integrated with Legend Pay to provide institutional clients with a seamless and secure way to connect their bank accounts and buy/sell digital assets. With various payment options available, including Swift, Wire, SEPA, and Faster Payments, the collaboration streamlines the crypto trading process, offering competitive pricing and deep liquidity for institutional investors, and improving their flexibility and security in managing digital assets.",
      list: [
        {
          title: "KYC Connect:",
          content:
            "Streamline the fiat onramp and offramp  for your institutional clients with KYC Connect, eliminating the hassle of duplicate onboarding procedures. With just one click, users can begin to buy and sell crypto instantly."
        },
        {
          title: "Institutional-Grade Trading Services:",
          content:
            "Our services provide customers with the best pricing to buy and sell cryptocurrency. This advantage can help to attract more sophisticated investors who are looking for high-quality trading services."
        },
        {
          title: "Support all major fiat currencies:",
          content:
            "Legend Paysupports major currencies including USD, EUR, GBP, AUD, CHF, CAD, JPY, and more."
        }
      ]
    },
    introduce: {
      title: "About Cobo",
      subTitle:
        "Cobo is a global cryptocurrency wallet and custody solution provider that offers a suite of digital asset management tools for individuals, institutions, and enterprises. With its state-of-the-art security features, multi-chain support, and user-friendly interface, Cobo provides a secure and seamless experience for managing a wide range of digital assets. Founded in 2017, Cobo has quickly grown to become one of the leading cryptocurrency custodians in the industry, trusted by thousands of clients worldwide. Cobo's mission is to make cryptocurrency accessible to everyone by providing reliable and innovative solutions for securely managing digital assets."
    },
    href: "https://cobo.com/"
  },
  {
    key: "bitrue",
    name: "Bitrue",
    primaryColors: {
      color: "#f8db43",
      color01: "rgba(248, 219, 67, 0.1)"
    },
    smallView: require(`./assets/img/cases/bitrue-view-small.png`),
    logo: require(`./assets/logo-partners/Exchanges Wallets/Bitrue.png`),
    desktop: require("./assets/img/cases/bitrue-desktop.png"),
    mobile: require("./assets/img/cases/bitrue-mobile.png"),
    desc:
      "Bitrue has integrated Legend Pay to provide a streamlined method for users to buy and sell cryptocurrencies. This integration enables Bitrue users to easily purchase and sell cryptocurrencies using their bank accounts or credit cards. With this partnership, users can enter the cryptocurrency market more easily, and benefit from a secure and user-friendly platform for their trading needs.",
    title: "Effortlessly Purchase and Sell Cryptocurrencies",
    subTitle:
      "Bitrue has teamed up with Legend Trading to enable their users to connect their bank to buy or sell crypto in a safe and simple manner directly on the Bitrue platform for their respective customers.",
    integration: {
      title:
        "Bitrue has integrated Legend Pay to provide a streamlined method for users to buy and sell cryptocurrencies. This integration enables Bitrue users to easily purchase and sell cryptocurrencies using their bank accounts or credit cards. With this partnership, users can enter the cryptocurrency market more easily, and benefit from a secure and user-friendly platform for their trading needs.",
      list: [
        {
          title: "KYC Connect:",
          content:
            "We offers a range of payment options, including bank transfers and credit cards such as Wire, Swift, SEPA, Faster Payments, Visa, and MasterCard for customer convenience."
        },
        {
          title: "Institutional-Grade Trading Services:",
          content:
            "Streamline the fiat onramp and offramp  for your institutional clients with KYC Connect, eliminating the hassle of duplicate onboarding procedures. With just one click, users can begin to buy and sell crypto instantly."
        },
        {
          title: "Support all major fiat currencies:",
          content:
            "We offer global coverage and accept all payment methods, including major local options. With support for multiple fiat currencies, including USD, EUR, GBP, AUD, CHF, CAD, JPY, and more, we operate in over 150 jurisdictions."
        }
      ]
    },
    introduce: {
      title: "About Bitrue",
      subTitle:
        "Bitrue is a cryptocurrency exchange platform founded in 2018, with headquarters in Singapore. The platform offers a wide range of services, including cryptocurrency trading, loans, and staking. It is known for its user-friendly interface, relatively low fees, and support for a variety of cryptocurrencies and trading pairs. Bitrue aims to provide a secure and convenient trading environment for its users and has implemented a number of security measures to help protect users' assets and information."
    },
    href: "https://www.bitrue.com/"
  },
  {
    key: "ellipal",
    name: "Ellipal",
    primaryColors: {
      color: "#307cf9",
      color01: "rgba(48, 124, 249, 0.1)"
    },
    smallView: require(`./assets/img/cases/ellipal-view-small.png`),
    logo: require(`./assets/logo-partners/Exchanges Wallets/Ellipal.png`),
    desktop: require("./assets/img/cases/ellipal-desktop.png"),
    mobile: require("./assets/img/cases/ellipal-mobile.png"),
    desc:
      "Ellipal's crypto wallet has now joined forces with Legend Pay's fiat service, making it simpler for users to purchase and sell cryptocurrencies using their bank accounts or credit cards. By providing institutional-level trading services, Legend Pay guarantees that users receive optimal pricing and substantial liquidity. This collaboration facilitates the execution of single trades up to $20 million in size while adhering to regulatory requirements.",
    title: "Unleash the Complete Scope of Expansion",
    subTitle:
      "Ellipal has collaborated with Legend Trading, allowing users to conveniently and safely purchase or sell digital currencies using their bank accounts or credit cards directly through the Ellipal wallet.",
    integration: {
      title:
        "Ellipal's crypto wallet has now joined forces with Legend Pay's fiat service, making it simpler for users to purchase and sell cryptocurrencies using their bank accounts or credit cards. By providing institutional-level trading services, Legend Pay guarantees that users receive optimal pricing and substantial liquidity. This collaboration facilitates the execution of single trades up to $20 million in size while adhering to regulatory requirements.",
      list: [
        {
          title: "Convenient Payment Options:",
          content:
            "We offer diverse payment options such as bank transfers and credit cards(Wire, Swift, SEPA, Faster Payments, Visa, and MasterCard)."
        },
        {
          title: "The most competitive pricing:",
          content:
            "Leveraging Legend's proprietary trading technology, we provide compliant access to the most competitive pricing for fiat-to-crypto trades."
        },
        {
          title: "Broad Currency Selection:",
          content:
            "By accommodating numerous major fiat currencies, clients are presented with a diverse array of choices, which simplifies the process of purchasing cryptocurrencies in their native currency."
        }
      ]
    },
    introduce: {
      title: "About Ellipal",
      subTitle:
        "Ellipal is a technology company focused on developing secure and user-friendly cryptocurrency hardware wallets and related solutions. Their flagship product is the Ellipal Titan, a hardware wallet designed to provide top-notch security and ease of use for storing, managing, and trading cryptocurrencies. The wallet supports a wide range of digital assets and offers features such as QR code-based transactions, a touchscreen interface, and an air-gapped design to protect users' private keys from unauthorized access."
    },
    href: "https://www.ellipal.com/"
  },
  {
    key: "bitmart",
    name: "BitMart",
    primaryColors: {
      color: "#16c2c2",
      color01: "rgba(22, 194, 194, 0.1)"
    },
    smallView: require(`./assets/img/cases/bitmart-view-small.png`),
    logo: require(`./assets/logo-partners/Exchanges Wallets/BitMart.png`),
    desktop: require("./assets/img/cases/bitmart-desktop.png"),
    mobile: require("./assets/img/cases/bitmart-mobile.png"),
    desc:
      "BitMart and Legend Trading have joined forces to allow users to link their bank accounts for secure and straightforward buying or selling of cryptocurrencies directly on the BitMart platform, catering to the needs of their individual customers.",
    title: "Connect Banking Into Your Crypto Experience",
    subTitle:
      "BitMart and Legend Trading have joined forces to allow users to link their bank accounts for secure and straightforward buying or selling of cryptocurrencies directly on the BitMart platform, catering to the needs of their individual customers.",
    integration: {
      title:
        "Bitmart and Legend Trading have joined forces to create a convenient solution for purchasing and selling digital currencies, enabling users to connect their bank accounts to cryptocurrency. This collaboration also delivers competitive rates for acquiring crypto assets. Furthermore, Legend Pay's fiat service offers various payment options, encompassing bank transfers and widely accepted credit cards like Visa and MasterCard.",
      list: [
        {
          title: "Convenient Payment Options:",
          content:
            "We offer diverse payment options such as bank transfers and credit cards(Wire, Swift, SEPA, Faster Payments, Visa, and MasterCard)."
        },
        {
          title: "Global coverage and local payment methods:",
          content:
            "We offer global coverage and accept all payment methods, including major local options. With support for multiple fiat currencies, including USD, EUR, GBP, AUD, CHF, CAD, JPY, and more, we operate in over 150 jurisdictions."
        },
        {
          title: "Institutional-Grade Trading Services:",
          content:
            "Our offerings grant clients optimal pricing for purchasing and selling digital currencies. This benefit has the potential to draw the attention of more discerning investors seeking top-tier trading solutions."
        }
      ]
    },
    introduce: {
      title: "About BitMart",
      subTitle:
        "BitMart is a global digital asset trading platform that offers a variety of cryptocurrency trading services to users worldwide. Launched in 2018, it aims to provide a secure, reliable, and user-friendly platform for trading various cryptocurrencies, including Bitcoin, Ethereum, and many others. BitMart focuses on offering advanced trading features, a wide range of supported digital assets, and competitive fees to cater to both novice and experienced traders. The platform is committed to maintaining high standards of security and compliance to protect users' assets and ensure a trustworthy trading environment."
    },
    href: "https://www.bitmart.com/"
  },
  {
    key: "zoomex",
    name: "ZOOMEX",
    primaryColors: {
      color: "#24e3d4",
      color01: "rgba(36, 227, 212, 0.1)"
    },
    smallView: require(`./assets/img/cases/zoomex-view-small.png`),
    logo: require(`./assets/logo-partners/Exchanges Wallets/Zoomex.png`),
    desktop: require("./assets/img/cases/zoomex-desktop.png"),
    mobile: require("./assets/img/cases/zoomex-mobile.png"),
    desc:
      "Zoomex has integrated Legend Pay to create a simplified process for users to buy and sell cryptocurrencies. This integration allows Zoomex users to conveniently purchase and sell digital assets using their bank accounts or credit cards. With this collaboration, users can easily enter the cryptocurrency market and take advantage of a secure and user-friendly platform to fulfill their trading needs.",
    title: "The premier fiat gateway for crypto transactions",
    subTitle:
      "Get ready for expert-level crypto trading! Zoomex and Legend Trading have partnered to facilitate connecting your bank account to digital currencies. This provides a comprehensive platform for buying and selling cryptocurrencies, along with the advantage of attractive pricing when acquiring crypto assets.",
    integration: {
      title:
        "Zoomex has integrated Legend Pay to create a simplified process for users to buy and sell cryptocurrencies. This integration allows Zoomex users to conveniently purchase and sell digital assets using their bank accounts or credit cards. With this collaboration, users can easily enter the cryptocurrency market and take advantage of a secure and user-friendly platform to fulfill their trading needs.",
      list: [
        {
          title: "",
          content:
            "We provide customers with a variety of payment options, including bank transfers and credit cards such as Wire, Swift, SEPA, Faster Payments, Visa, and MasterCard, for their convenience."
        },
        {
          title: "",
          content:
            "Our trading services offer institutional-level support, featuring competitive pricing that can appeal to discerning investors in search of high-quality trading solutions."
        },
        {
          title: "",
          content:
            " Support numerous major fiat currencies, presenting customers with a diverse array of choices for purchasing cryptocurrencies in their native currency. This facilitates the process of buying digital assets for customers."
        }
      ]
    },
    introduce: {
      title: "About Zoomex",
      subTitle:
        "Zoomex is a cryptocurrency trading platform that allows users to buy and sell digital assets with ease and security. The platform supports a range of cryptocurrencies, including Bitcoin, Ethereum, Litecoin, and other major digital assets, and offers multiple payment options for user convenience, including bank transfers and credit cards. Zoomex also provides institutional-grade trading services with competitive pricing, appealing to sophisticated investors seeking high-quality trading solutions. The platform is committed to ensuring customer satisfaction and offering a user-friendly and transparent trading experience."
    },
    href: "https://www.zoomex.com/en-US/"
  },
  {
    key: "lbank",
    name: "LBANK",
    primaryColors: {
      color: "#d7bd5e",
      color01: "rgba(215, 189, 94, 0.1)"
    },
    smallView: require(`./assets/img/cases/lbank-view-small.png`),
    logo: require(`./assets/logo-partners/Exchanges Wallets/LBANK.png`),
    desktop: require("./assets/img/cases/lbank-desktop.png"),
    mobile: require("./assets/img/cases/lbank-mobile.png"),
    desc:
      "LBank's collaboration with Legend Pay's fiat service allows for the effortless purchase and sale of cryptocurrencies using bank accounts or credit cards. Legend Pay provides institutional-level trading services, guaranteeing users access to optimal pricing and substantial liquidity. This collaboration permits single trades of up to $20 million in size while remaining compliant with regulatory requirements.",
    title: "Purchase Crypto using Bank Accounts and Cards",
    subTitle:
      "LBank and Legend Trading have partnered to enable the secure and straightforward purchasing and selling of cryptocurrencies via bank accounts or credit cards directly on the LBank platform, catering to the needs of their individual customers.",
    integration: {
      title:
        "LBank's collaboration with Legend Pay's fiat service allows for the effortless purchase and sale of cryptocurrencies using bank accounts or credit cards. Legend Pay provides institutional-level trading services, guaranteeing users access to optimal pricing and substantial liquidity. This collaboration permits single trades of up to $20 million in size while remaining compliant with regulatory requirements.",
      list: [
        {
          title: "Convenient Payment Options:",
          content:
            "We provide various payment options, including bank transfers and credit cards such as Wire, Swift, SEPA, Faster Payments, Visa, and MasterCard, for customer convenience."
        },
        {
          title: "The most competitive pricing:",
          content:
            "Our trading services offer institutional-level support, featuring competitive pricing that can appeal to discerning investors in search of high-quality trading solutions."
        },
        {
          title: "Global coverage and local payment methods:",
          content:
            "Our platform supports numerous major fiat currencies, presenting customers with a diverse array of choices for purchasing cryptocurrencies in their native currency. This facilitates the process of buying digital assets for customers."
        }
      ]
    },
    introduce: {
      title: "About LBank",
      subTitle:
        "LBank is a cryptocurrency exchange platform that offers users the ability to buy, sell, and trade various digital assets, including Bitcoin, Ethereum, and many other altcoins. Launched in 2016, LBank is headquartered in Singapore and has a global user base. The platform offers a range of trading features, including real-time market data, advanced trading tools, and secure storage for digital assets. LBank is committed to providing users with a safe and user-friendly platform for cryptocurrency trading, with a focus on transparency and customer satisfaction."
    },
    href: "https://www.lbank.info/"
  },
  {
    key: "xbank",
    name: "xBank",
    primaryColors: {
      color: "#0202fe",
      color01: "rgba(2, 2, 254, 0.1)"
    },
    smallView: require(`./assets/img/cases/xbank-view-small.png`),
    logo: require(`./assets/logo-partners/Exchanges Wallets/Xbank.png`),
    desktop: require("./assets/img/cases/xbank-desktop.png"),
    mobile: require("./assets/img/cases/xbank-mobile.png"),
    desc:
      "XBank has teamed up with Legend Trading to create a convenient and secure way for users to buy or sell cryptocurrencies using their bank accounts or credit cards directly through the XBank wallet. This collaboration offers a streamlined process for users to access the cryptocurrency market and take advantage of a user-friendly and secure platform for their trading needs.",
    title: "Comprehensive Fiat Support for Buying Crypto",
    subTitle:
      "XBank has teamed up with Legend Trading to create a convenient and secure way for users to buy or sell cryptocurrencies using their bank accounts or credit cards directly through the XBank wallet. This collaboration offers a streamlined process for users to access the cryptocurrency market and take advantage of a user-friendly and secure platform for their trading needs.",
    integration: {
      title:
        "XBank has teamed up with Legend Trading to create a convenient and secure way for users to buy or sell cryptocurrencies using their bank accounts or credit cards directly through the XBank wallet. This collaboration offers a streamlined process for users to access the cryptocurrency market and take advantage of a user-friendly and secure platform for their trading needs.",
      list: [
        {
          title: "Global Payment Options:",
          content:
            "Provide diverse payment methods, including bank transfers and credit cards such as Wire, Swift, SEPA, Faster Payments, Visa, and MasterCard."
        },
        {
          title: "The most competitive pricing:",
          content:
            "Leveraging Legend's proprietary trading technology, we provide compliant access to the most competitive pricing for fiat-to-crypto trades."
        },
        {
          title: "Comprehensive Fiat Currency Support:",
          content:
            "Legend Pay accommodates major currencies such as USD, EUR, GBP, AUD, CHF, CAD, JPY, and several others, providing users with a wide range of options to choose from."
        }
      ]
    },
    introduce: {
      title: "About XBank",
      subTitle:
        "XBank is a cryptocurrency trading and investment platform that allows users to trade various digital assets, including Bitcoin, Ethereum, and more. The platform also provides users with investment opportunities in other cryptocurrency-related projects and services. XBank aims to provide a secure, user-friendly, and innovative platform for users to participate in the growing cryptocurrency market."
    },
    href: "https://xbank.plus/"
  },
  {
    key: "bitpush",
    name: "Bitpush",
    primaryColors: {
      color: "#f6544b",
      color01: "rgba(246, 84, 75, 0.1)"
    },
    smallView: require(`./assets/img/cases/bitpush-view-small.png`),
    logo: require(`./assets/logo-partners/Exchanges Wallets/Bitpush.png`),
    desktop: require("./assets/img/cases/bitpush-desktop.png"),
    mobile: require("./assets/img/cases/bitpush-mobile.png"),
    desc:
      "Bitpush, a significant blockchain media platform, has collaborated with Legend Pay's fiat service to facilitate easy buying and selling of cryptocurrencies using bank accounts or credit cards. Legend Pay offers institutional-level trading services, guaranteeing users the best pricing and substantial liquidity. This collaboration enables single trades of up to $20 million in size while adhering to regulatory compliance.",
    title: "The Fiat On-ramp for Crypto Transactions",
    subTitle:
      "Bitpush has integrated with Legend Pay to allow its users to link their bank accounts for secure and straightforward buying or selling of cryptocurrencies directly on the Bitpush platform, catering to the needs of their respective customers.",
    integration: {
      title:
        "Bitpush, a significant blockchain media platform, has collaborated with Legend Pay's fiat service to facilitate easy buying and selling of cryptocurrencies using bank accounts or credit cards. Legend Pay offers institutional-level trading services, guaranteeing users the best pricing and substantial liquidity. This collaboration enables single trades of up to $20 million in size while adhering to regulatory compliance.",
      list: [
        {
          title: "",
          content:
            "We provide a variety of payment options to ensure customer convenience, including bank transfers and credit cards such as Wire, Swift, SEPA, Faster Payments, Visa, and MasterCard."
        },
        {
          title: "",
          content:
            "Provide institutional-grade trading services that feature competitive pricing, catering to discerning investors seeking high-quality trading solutions."
        },
        {
          title: "",
          content:
            "Offer global coverage and accept all major payment methods, including local options, operating in over 150 jurisdictions. With support for multiple fiat currencies, including USD, EUR, GBP, AUD, CHF, CAD, JPY, and more, we strive to cater to customers worldwide."
        }
      ]
    },
    introduce: {
      title: "About Bitpush",
      subTitle:
        "Bitpush is a blockchain and cryptocurrency media platform that delivers up-to-date news, analysis, and market data to its audience. Founded in 2018, Bitpush aims to provide unbiased coverage and insights into the rapidly evolving world of blockchain and cryptocurrency. The platform covers a range of topics, including blockchain technology, cryptocurrencies, regulations, and industry trends. Bitpush is dedicated to providing reliable and informative content to its users, catering to both beginner and experienced audiences."
    },
    href: "https://en.bitpush.news/"
  },
  {
    key: "blocto",
    name: "Blocto",
    primaryColors: {
      color: "#307cf9",
      color01: "rgba(48, 124, 249, 0.1)"
    },
    smallView: "",
    logo: require(`./assets/logo-partners/Exchanges Wallets/Blocto.png`),
    desc: "",
    href: "https://blocto.io/"
  },
  {
    key: "cointiger",
    name: "COINTIGER",
    primaryColors: {
      color: "#307cf9",
      color01: "rgba(48, 124, 249, 0.1)"
    },
    smallView: "",
    logo: require(`./assets/logo-partners/Exchanges Wallets/CoinTiger.png`),
    desc: "",
    href: "https://www.cointiger.com/en-us/#/index"
  },
  {
    key: "coinw",
    name: "CoinW",
    primaryColors: {
      color: "#307cf9",
      color01: "rgba(48, 124, 249, 0.1)"
    },
    smallView: "",
    logo: require(`./assets/logo-partners/Exchanges Wallets/CoinW.png`),
    desc: "",
    href: "https://www.coinw.com/"
  },
  {
    key: "probit",
    name: "PROBIT",
    primaryColors: {
      color: "#307cf9",
      color01: "rgba(48, 124, 249, 0.1)"
    },
    smallView: "",
    logo: require(`./assets/logo-partners/Exchanges Wallets/ProBit.png`),
    desc: "",
    href: "https://www.probit.com/en-us/"
  },
  {
    key: "valens",
    name: "Valens Exchange",
    primaryColors: {
      color: "#307cf9",
      color01: "rgba(48, 124, 249, 0.1)"
    },
    smallView: "",
    logo: require(`./assets/logo-partners/Exchanges Wallets/Valens Exchange.png`),
    desc: "",
    href: "https://valens-exchange.com/"
  },
  {
    key: "bingx",
    name: "BingX",
    primaryColors: {
      color: "#307cf9",
      color01: "rgba(48, 124, 249, 0.1)"
    },
    smallView: "",
    logo: require(`./assets/logo-partners/Exchanges Wallets/BingX.png`),
    desc: "",
    href: "https://bingx.com/en-us"
  },
  {
    key: "deribit",
    name: "Deribit",
    primaryColors: {
      color: "#307cf9",
      color01: "rgba(48, 124, 249, 0.1)"
    },
    smallView: "",
    logo: require(`./assets/logo-partners/Exchanges Wallets/Deribit.png`),
    desc: "",
    href: "https://www.deribit.com/"
  }
];

const bannerOptionList = [
  {
    key: "kucoin",
    primaryColors: {
      color: "#23af91"
    },
    logo: require(`./assets/logo-partners/Exchanges Wallets/kucoin-white.svg`),
    bannerView: require(`./assets/img/cases/kucoin-view.png`),
    introduce:
      "The integration with Legend Trading allows Kucoin users to trade cryptocurrencies with fast fiat settlements."
  },
  {
    key: "gateio",
    primaryColors: {
      color: "#2354e6"
    },
    logo: require(`./assets/logo-partners/Exchanges Wallets/gateio-white.svg`),
    bannerView: require(`./assets/img/cases/gateio-view.png`),
    introduce:
      "By integrating our solution, gate.io allows its millions of users to purchase digital assets via bank transfers seamlessly…"
  }
  // {
  //   key: "bybit",
  //   primaryColors: {
  //     color: "#15192a"
  //   },
  //   logo: require(`./assets/logo-partners/Exchanges Wallets/bybit-white.svg`),
  //   bannerView: require(`./assets/img/cases/bybit-view.png`),
  //   introduce:
  //     "By integrating our solution, Bybit allows its millions of users to purchase digital assets via bank transfers seamlessly and receive…"
  // }
];

export {
  logSuccess,
  logError,
  displayErrors,
  dateConfig,
  getStartDate,
  apiConfig,
  getCountryList,
  highRiskTelCountries,
  partnerOptionList,
  bannerOptionList
};

export default [logSuccess, logError];
